import React, { useState, useEffect, useRef } from 'react';
import { Card, Tree, Spin, Typography, Space, Collapse, Button, Drawer, message } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { api } from '../../services/api';

const { Title, Text } = Typography;

const FilterSidebar = ({
  runDateTime,
  selectedRegions,
  initialCheckedKeys,
  onSelectionChange,
  collapsed,
  setCollapsed,
  isMobile,
  children
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);

  // Track if we've done the initial load using a ref (doesn't cause re-renders)
  const hasInitializedRef = useRef(false);

  // Track previous values to prevent unnecessary refetches
  const prevValuesRef = useRef({ runDateTime: null, selectedRegions: [] });

  // Fetch hierarchy tree when run_datetime or selectedRegions change
  useEffect(() => {
    if (!runDateTime) return;

    // Check if values actually changed
    const regionsChanged = JSON.stringify(prevValuesRef.current.selectedRegions) !== JSON.stringify(selectedRegions);
    const runDateTimeChanged = prevValuesRef.current.runDateTime !== runDateTime;

    if (!hasInitializedRef.current || runDateTimeChanged || regionsChanged) {
      const fetchHierarchyTree = async () => {
        setLoading(true);
        try {
          const data = await api.getHierarchyTree(runDateTime, selectedRegions);
          setTreeData(data);

          // Only set checked keys on initial load
          if (!hasInitializedRef.current) {
            setExpandedKeys([]);

            const { keys: allKeys, nodes: allNodes } = collectAllKeysAndNodes(data);

            // Restore saved checked keys if available, otherwise select all
            let keysToUse;
            if (initialCheckedKeys && initialCheckedKeys.length > 0) {
              const allKeysSet = new Set(allKeys);
              const validKeys = initialCheckedKeys.filter(k => allKeysSet.has(k));
              keysToUse = validKeys.length > 0 ? validKeys : allKeys;
            } else {
              keysToUse = allKeys;
            }

            setCheckedKeys(keysToUse);

            // Notify parent with restored/default selections
            const keysSet = new Set(keysToUse);
            const nodesToUse = allNodes.filter(n => keysSet.has(n.key));
            const selection = extractSelectionData(keysToUse, nodesToUse);
            onSelectionChange(selection);

            hasInitializedRef.current = true;
          }

          // Update prev values
          prevValuesRef.current = { runDateTime, selectedRegions };
        } catch (error) {
          console.error("Failed to fetch hierarchy tree:", error);
          message.error("Failed to load filters");
        } finally {
          setLoading(false);
        }
      };

      fetchHierarchyTree();
    }
  }, [runDateTime, selectedRegions]);

  // Handle check/uncheck in the tree
  const onCheck = (checkedKeysValue, info) => {
    const keysArray = Array.isArray(checkedKeysValue) ? checkedKeysValue : checkedKeysValue.checked;
    setCheckedKeys(keysArray);

    const selection = extractSelectionData(keysArray, info.checkedNodes);
    onSelectionChange(selection);
  };

  // Extract region_ids, portfolio_ids, station_ids, duids from checked nodes
  const extractSelectionData = (keys, nodes) => {
    const regions = new Set();
    const portfolios = new Set();
    const stations = new Set();
    const duids = new Set();

    // Helper function to recursively collect all descendant IDs from a node
    const collectDescendants = (node) => {
      if (!node) return;

      const actualNode = node.node || node.props || node;
      const { type, region_id, portfolio_id, station_id, duid, children } = actualNode;

      // Collect this node's ID
      if (type === 'region') {
        regions.add(region_id || actualNode.key);
      } else if (type === 'portfolio') {
        portfolios.add(portfolio_id);
      } else if (type === 'station') {
        stations.add(station_id);
      } else if (type === 'duid') {
        duids.add(duid);
      }

      // Recursively collect from all children
      if (children && children.length > 0) {
        children.forEach(child => {
          collectDescendants(child);
        });
      }
    };

    // Process all checked nodes and their descendants
    nodes.forEach(node => {
      collectDescendants(node);
    });

    return {
      region_ids: Array.from(regions).filter(Boolean),
      portfolio_ids: Array.from(portfolios).filter(Boolean),
      station_ids: Array.from(stations).filter(Boolean),
      duids: Array.from(duids).filter(Boolean),
      checkedKeys: keys
    };
  };

  // Handle expand/collapse
  const onExpand = (expandedKeysValue) => {
    setExpandedKeys(expandedKeysValue);
  };

  // Collect all keys and nodes from tree data recursively
  const collectAllKeysAndNodes = (treeNodes) => {
    const keys = [];
    const nodes = [];
    const traverse = (items) => {
      items.forEach(node => {
        keys.push(node.key);
        nodes.push(node);
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      });
    };
    traverse(treeNodes);
    return { keys, nodes };
  };

  // Handle Select All
  const handleSelectAll = () => {
    const { keys, nodes } = collectAllKeysAndNodes(treeData);
    setCheckedKeys(keys);
    const selection = extractSelectionData(keys, nodes);
    onSelectionChange(selection);
  };

  // Handle Clear All
  const handleClearAll = () => {
    setCheckedKeys([]);
    onSelectionChange({
      region_ids: [],
      portfolio_ids: [],
      station_ids: [],
      duids: [],
      checkedKeys: []
    });
  };

  const filterContent = (
    <Space direction="vertical" style={{ width: '100%' }} size="small">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0, color: '#143660' }}>Filters</Title>
        <Space size="small">
          <Button
            size="small"
            onClick={handleSelectAll}
            disabled={loading}
            style={{
              backgroundColor: '#13a89e',
              borderColor: '#13a89e',
              color: 'white'
            }}
          >
            Select All
          </Button>
          <Button
            size="small"
            onClick={handleClearAll}
            disabled={loading}
            style={{
              backgroundColor: '#7396bf',
              borderColor: '#7396bf',
              color: 'white'
            }}
          >
            Clear
          </Button>
        </Space>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin tip="Loading filters..." />
        </div>
      ) : isMobile ? (
        <Tree
          checkable
          checkStrictly={false}
          onCheck={onCheck}
          checkedKeys={checkedKeys}
          expandedKeys={expandedKeys}
          onExpand={onExpand}
          treeData={treeData}
          titleRender={(nodeData) => <span>{nodeData.title}</span>}
          showLine={true}
          height={window.innerHeight - 160}
          style={{ marginTop: 8 }}
        />
      ) : (
        <Collapse
          defaultActiveKey={['1']}
          ghost
          items={[{
            key: '1',
            label: 'Hierarchy Filter',
            children: (
              <Tree
                checkable
                checkStrictly={false}
                onCheck={onCheck}
                checkedKeys={checkedKeys}
                expandedKeys={expandedKeys}
                onExpand={onExpand}
                treeData={treeData}
                titleRender={(nodeData) => <span>{nodeData.title}</span>}
                showLine={true}
                height={600}
                style={{ marginTop: 8 }}
              />
            )
          }]}
        />
      )}
    </Space>
  );

  if (isMobile) {
    return (
      <>
        <Button
          icon={<FilterOutlined />}
          onClick={() => setDrawerOpen(!drawerOpen)}
          style={{
            position: 'fixed',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1001,
            borderRadius: '0 4px 4px 0',
            padding: '20px 8px',
            height: 'auto',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            backgroundColor: '#13a89e',
            borderColor: '#13a89e',
            color: 'white'
          }}
        />
        <Drawer
          title={null}
          placement="left"
          width="85vw"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          closable={false}
          styles={{ body: { padding: '12px 36px' } }}
        >
          {children && (
            <>
              {children}
              <div style={{ borderTop: '1px solid #f0f0f0', margin: '16px 0' }} />
            </>
          )}
          {filterContent}
        </Drawer>
      </>
    );
  }

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <Card
        style={{
          height: '100%',
          overflow: 'auto',
          position: 'sticky',
          top: 24,
          minWidth: 250,
          borderLeft: '3px solid #143660',
          opacity: collapsed ? 0 : 1,
          transition: 'opacity 0.2s ease'
        }}
        styles={{
          body: {
            display: collapsed ? 'none' : 'block',
          }
        }}
      >
        {filterContent}
      </Card>
    </div>
  );
};

export default FilterSidebar;
